import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { taskNotificationsAPI, TaskNotification } from '../services/api';
import { showCustomAlert } from './CustomAlert';

const TaskNotifications: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadNotifications();
  }, [pagination.page]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await taskNotificationsAPI.getAll({
        page: pagination.page,
        limit: pagination.limit
      });
      setNotifications(data.notifications);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total,
        pages: data.pagination.pages
      }));
    } catch (err: any) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await taskNotificationsAPI.markAllAsRead();
      window.dispatchEvent(new Event('task-notifications-updated'));
      // Reset pagination to first page
      setPagination(prev => ({ ...prev, page: 1 }));
      await loadNotifications();
    } catch (err: any) {
      console.error('Error marking all as read:', err);
      showCustomAlert.error(err.response?.data?.message || 'Greška pri označavanju notifikacija');
    }
  };

  const handleDeleteAll = async () => {
    showCustomAlert.confirm(
      'Da li ste sigurni da želite da obrišete sve notifikacije?',
      async () => {
        try {
          await taskNotificationsAPI.deleteAll();
          window.dispatchEvent(new Event('task-notifications-updated'));
          // Reset pagination to first page after deletion
          setPagination(prev => ({ ...prev, page: 1 }));
          await loadNotifications();
        } catch (err: any) {
          console.error('Error deleting all notifications:', err);
          showCustomAlert.error(err.response?.data?.message || 'Greška pri brisanju notifikacija');
        }
      },
      'Potvrda brisanja'
    );
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await taskNotificationsAPI.markAsRead(id);
      window.dispatchEvent(new Event('task-notifications-updated'));
      await loadNotifications();
    } catch (err: any) {
      console.error('Error marking as read:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await taskNotificationsAPI.delete(id);
      window.dispatchEvent(new Event('task-notifications-updated'));
      await loadNotifications();
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      showCustomAlert.error(err.response?.data?.message || 'Greška pri brisanju notifikacije');
    }
  };

  const handleView = async (notification: TaskNotification) => {
    try {
      // Označi notifikaciju kao pročitanu kada korisnik pogleda zadatak/projekat
      await taskNotificationsAPI.markAsRead(notification._id);
      window.dispatchEvent(new Event('task-notifications-updated'));
    } catch (err: any) {
      console.error('Error marking notification as read on view:', err);
    }

    if (notification.taskId && notification.projectId) {
      navigate(`/projects/${notification.projectId._id}`);
    }
  };

  const typeLabels: Record<string, string> = {
    task_assigned: 'Zadatak dodeljen',
    task_updated: 'Zadatak ažuriran',
    task_completed: 'Zadatak završen',
    task_deleted: 'Zadatak obrisan',
    task_status_changed: 'Status promenjen',
    project_assigned: 'Projekat dodeljen',
    project_status_changed: 'Status projekta promenjen',
    comment_added: 'Novi komentar',
    procurement_request: 'Zahtev za nabavku',
    procurement_approved: 'Zahtev odobren',
    procurement_rejected: 'Zahtev odbijen',
    procurement_delivered: 'Roba isporučena',
    procurement_verified: 'Isporuka potvrđena',
    procurement_delivery_rejected: 'Isporuka odbijena',
    announcement_published: 'Obaveštenje objavljeno'
  };

  const typeSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    task_assigned: 'info',
    task_updated: 'info',
    task_completed: 'success',
    task_deleted: 'danger',
    task_status_changed: 'warning',
    project_assigned: 'info',
    project_status_changed: 'warning',
    comment_added: 'info',
    procurement_request: 'info',
    procurement_approved: 'success',
    procurement_rejected: 'danger',
    procurement_delivered: 'success',
    procurement_verified: 'success',
    procurement_delivery_rejected: 'danger',
    announcement_published: 'info'
  };

  const typeBodyTemplate = (rowData: TaskNotification) => {
    return (
      <Tag 
        value={typeLabels[rowData.type] || rowData.type} 
        severity={typeSeverity[rowData.type] || 'info'}
      />
    );
  };

  const dateBodyTemplate = (rowData: TaskNotification) => {
    const date = new Date(rowData.createdAt);
    return `${date.toLocaleDateString('sr-RS')} ${date.toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const actionsBodyTemplate = (rowData: TaskNotification) => {
    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {rowData.taskId && rowData.projectId && (
          <Button
            icon="pi pi-eye"
            className="p-button-text"
            onClick={() => handleView(rowData)}
            aria-label="Pogledaj"
          />
        )}
        <Button
          icon="pi pi-trash"
          className="p-button-text"
          onClick={() => handleDelete(rowData._id)}
          aria-label="Obriši"
        />
      </div>
    );
  };

  const filteredNotifications = notifications.filter(n => 
    n.message.toLowerCase().includes(filter.toLowerCase()) ||
    (n.taskId?.name?.toLowerCase().includes(filter.toLowerCase())) ||
    (n.projectId?.name?.toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: 0, marginBottom: '1rem' }}>Notifikacije zadataka</h1>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <span className="p-input-icon-left" style={{ flex: 1, minWidth: '250px' }}>
            <i className="pi pi-search" style={{ left: '0.75rem' }} />
            <InputText
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtriraj notifikacije..."
              style={{ width: '100%', paddingLeft: '2.5rem' }}
            />
          </span>
          
          <Button
            label="Označi sve kao pročitane"
            icon="pi pi-check"
            onClick={handleMarkAllAsRead}
            disabled={pagination.total === 0}
          />
          
          <Button
            label="Obriši sve"
            icon="pi pi-trash"
            severity="danger"
            onClick={handleDeleteAll}
            disabled={pagination.total === 0}
          />
        </div>
      </div>

      <DataTable
        value={filteredNotifications}
        loading={loading}
        paginator
        rows={pagination.limit}
        totalRecords={pagination.total}
        first={(pagination.page - 1) * pagination.limit}
        onPage={(e) => setPagination(prev => ({ ...prev, page: (e.page || 0) + 1 }))}
        emptyMessage="Nema notifikacija"
        rowClassName={(rowData) => rowData.isRead ? '' : 'unread-notification'}
      >
        <Column field="type" header="Tip" body={typeBodyTemplate} sortable />
        <Column field="message" header="Poruka" sortable />
        <Column field="createdAt" header="Datum i vreme" body={dateBodyTemplate} sortable />
        <Column body={actionsBodyTemplate} header="Akcije" style={{ width: '150px' }} />
      </DataTable>
    </div>
  );
};

export default TaskNotifications;

